import { Component, ElementRef, HostListener, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { ConsultaService } from '../../services/consulta.service';
import { BeniChat } from './beni-chat.config';

interface Mensaje {
  de: 'beni' | 'yo' | 'aviso';
  texto: string;
}

/**
 * Chat de Beni (agente de ElevenLabs en modo solo texto) como botón flotante.
 * El AppComponent lo crea solo con la sesión iniciada y lo destruye al cerrarla.
 *
 * Habla con el agente por websocket; la conversación se abre la primera vez que
 * se despliega el panel. El agente solo acepta conexiones desde los dominios de
 * la app y verifica la reserva con teléfono y nombre del titular: el nombre de
 * la sesión solo se usa para el saludo.
 */
@Component({
  selector: 'app-beni-chat',
  templateUrl: './beni-chat.component.html',
  styleUrls: ['./beni-chat.component.scss'],
})
export class BeniChatComponent implements OnInit, OnDestroy {

  @ViewChild('lista') lista: ElementRef;

  public abierto = false;
  public escribiendo = false;
  public conectado = false;
  public terminada = false;
  public mensajes: Mensaje[] = [];
  public texto = '';

  // Separación del borde inferior: por encima del pie de la página visible, si lo tiene,
  // para no tapar sus botones (Continuar, Pagar…).
  public bottom = 16;
  private rutas: Subscription;
  private medidas: any[] = [];

  // Nota de voz: se graba, se transcribe en la API de AsistencIA y el texto
  // queda en la caja para que el usuario lo revise antes de enviarlo.
  public grabando = false;
  public transcribiendo = false;
  public segundos = 0;
  public readonly puedeGrabar = !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia && (window as any).MediaRecorder);

  private ws: WebSocket;
  private pendientes: string[] = [];
  private grabadora: any;
  private trozos: Blob[] = [];
  private reloj: any;
  private cancelada = false;

  constructor(private _consulta: ConsultaService, private zone: NgZone, private translate: TranslateService,
    private router: Router) { }

  ngOnInit() {
    this.recolocar();
    this.rutas = this.router.events.pipe(filter(e => e instanceof NavigationEnd)).subscribe(() => this.recolocar());
  }

  @HostListener('window:resize')
  recolocar() {
    // La página nueva entra con animación: se mide varias veces hasta que está colocada.
    this.medidas.forEach(t => clearTimeout(t));
    this.medidas = [0, 300, 800].map(ms => setTimeout(() => this.zone.run(() => this.medir()), ms));
  }

  private medir() {
    const margen = window.innerWidth <= 480 ? 8 : 16;
    let alto = 0;
    const pies = document.querySelectorAll('ion-router-outlet > .ion-page:not(.ion-page-hidden) ion-footer');
    for (let i = 0; i < pies.length; i++) {
      const r = (pies[i] as HTMLElement).getBoundingClientRect();
      if (r.height > 0 && r.bottom > window.innerHeight - 4) {
        alto = Math.max(alto, window.innerHeight - r.top);
      }
    }
    this.bottom = alto ? Math.round(alto) + margen : margen;
  }

  get activo(): boolean {
    return !!BeniChat.agentId;
  }

  async grabar() {
    if (this.grabando || this.transcribiendo) {
      return;
    }
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err) {
      this.avisar('sin_micro');
      return;
    }
    const MR = (window as any).MediaRecorder;
    const tipo = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus']
      .find(t => MR.isTypeSupported && MR.isTypeSupported(t));
    this.grabadora = tipo ? new MR(stream, { mimeType: tipo }) : new MR(stream);
    this.trozos = [];
    this.cancelada = false;
    this.grabadora.ondataavailable = (e: any) => {
      if (e.data && e.data.size) {
        this.trozos.push(e.data);
      }
    };
    this.grabadora.onstop = () => this.zone.run(() => {
      stream.getTracks().forEach(t => t.stop());
      clearInterval(this.reloj);
      this.grabando = false;
      if (!this.cancelada) {
        this.transcribir(new Blob(this.trozos, { type: this.grabadora.mimeType || tipo || 'audio/webm' }));
      }
    });
    this.grabadora.start();
    this.grabando = true;
    this.segundos = 0;
    this.reloj = setInterval(() => this.zone.run(() => {
      this.segundos++;
      if (this.segundos >= BeniChat.maxSegundosAudio) {
        this.pararGrabacion();
      }
    }), 1000);
  }

  pararGrabacion() {
    if (this.grabadora && this.grabadora.state !== 'inactive') {
      this.grabadora.stop();
    }
  }

  cancelarGrabacion() {
    this.cancelada = true;
    this.pararGrabacion();
  }

  get tiempo(): string {
    const s = this.segundos;
    return Math.floor(s / 60) + ':' + ('0' + (s % 60)).slice(-2);
  }

  private async transcribir(audio: Blob) {
    if (audio.size < 1000) {
      return; // pulsación sin habla
    }
    this.transcribiendo = true;
    try {
      const res = await fetch(BeniChat.transcribeUrl.replace(':agentId', encodeURIComponent(BeniChat.agentId)), {
        method: 'POST',
        headers: { 'Content-Type': audio.type || 'audio/webm' },
        body: audio,
      });
      const data = res.ok ? await res.json() : null;
      this.zone.run(() => {
        const texto = data && data.text ? String(data.text).trim() : '';
        if (texto) {
          this.texto = this.texto ? this.texto.trim() + ' ' + texto : texto;
        } else {
          this.avisar(res.status === 429 ? 'muchas_notas' : res.ok ? 'no_entendida' : 'error_nota');
        }
      });
    } catch (err) {
      this.zone.run(() => this.avisar('error_nota'));
    } finally {
      this.zone.run(() => this.transcribiendo = false);
    }
  }

  /** Aviso en el chat; `clave` es la de la sección BeniChat de assets/i18n. */
  private avisar(clave: string) {
    this.mensajes.push({ de: 'aviso', texto: this.translate.instant('BeniChat.' + clave) });
    this.bajar();
  }

  alternar() {
    this.abierto = !this.abierto;
    if (!this.abierto) {
      this.cancelarGrabacion();
    }
    if (this.abierto && !this.ws) {
      this.conectar();
    }
    this.bajar();
  }

  enviar() {
    const texto = (this.texto || '').trim();
    if (!texto || this.escribiendo) {
      return;
    }
    this.texto = '';
    this.mensajes.push({ de: 'yo', texto });
    this.escribiendo = true;
    this.bajar();
    if (this.conectado) {
      this.ws.send(JSON.stringify({ type: 'user_message', text: texto }));
    } else {
      // Se envía en cuanto el agente acepte la conversación.
      this.pendientes.push(texto);
      if (!this.ws) {
        this.conectar();
      }
    }
  }

  nuevaConversacion() {
    this.cerrarSocket();
    this.mensajes = [];
    this.pendientes = [];
    this.terminada = false;
    this.escribiendo = false;
    this.conectar();
  }

  ngOnDestroy() {
    if (this.rutas) {
      this.rutas.unsubscribe();
    }
    this.medidas.forEach(t => clearTimeout(t));
    this.cancelarGrabacion();
    this.cerrarSocket();
  }

  private conectar() {
    const nombre = [this._consulta.usuario.Nombre, this._consulta.usuario.Apellidos]
      .filter(x => !!x).join(' ');
    const ws = new WebSocket(BeniChat.url + '?agent_id=' + encodeURIComponent(BeniChat.agentId));
    this.ws = ws;
    this.escribiendo = true; // hasta que llegue el saludo

    ws.onopen = () => {
      ws.send(JSON.stringify({
        type: 'conversation_initiation_client_data',
        dynamic_variables: { nombre_usuario: nombre },
      }));
    };

    ws.onmessage = (e) => this.zone.run(() => {
      let m: any;
      try {
        m = JSON.parse(e.data);
      } catch (err) {
        return;
      }
      switch (m.type) {
        case 'conversation_initiation_metadata':
          this.conectado = true;
          this.pendientes.splice(0).forEach(t => ws.send(JSON.stringify({ type: 'user_message', text: t })));
          break;
        case 'agent_response':
          this.mensajes.push({ de: 'beni', texto: m.agent_response_event.agent_response });
          this.escribiendo = false;
          this.bajar();
          break;
        case 'agent_response_correction': {
          const ult = this.ultimoDeBeni();
          if (ult) {
            ult.texto = m.agent_response_correction_event.corrected_agent_response;
          }
          break;
        }
        case 'ping':
          ws.send(JSON.stringify({ type: 'pong', event_id: m.ping_event.event_id }));
          break;
      }
    });

    ws.onclose = () => this.zone.run(() => {
      if (this.ws !== ws) {
        return;
      }
      this.ws = null;
      this.conectado = false;
      if (this.escribiendo || this.pendientes.length) {
        this.mensajes.push({ de: 'aviso', texto: this.translate.instant('BeniChat.sin_conexion') });
      }
      this.escribiendo = false;
      this.pendientes = [];
      if (this.mensajes.some(x => x.de !== 'aviso')) {
        this.terminada = true;
      }
      this.bajar();
    });
  }

  private cerrarSocket() {
    if (this.ws) {
      const ws = this.ws;
      this.ws = null;
      this.conectado = false;
      try {
        ws.close();
      } catch (err) { }
    }
  }

  private ultimoDeBeni(): Mensaje {
    for (let i = this.mensajes.length - 1; i >= 0; i--) {
      if (this.mensajes[i].de === 'beni') {
        return this.mensajes[i];
      }
    }
    return null;
  }

  private bajar() {
    setTimeout(() => {
      const el = this.lista && this.lista.nativeElement;
      if (el) {
        el.scrollTop = el.scrollHeight;
      }
    }, 0);
  }
}
